/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: networkSettingApi.ts
 🗓️ Created: 2025-12-04
 🧩 Description:
    API client for Network Configuration module
    (Read network config from remote telephony servers)
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { ServerNetworkConfig } from '../types';

/**
 * Fetch network configuration for a specific server
 */
export async function fetchServerNetworkConfig(serverId: number | string): Promise<ServerNetworkConfig> {
  const response = await fetch(
    buildApiUrl(`/api/admin/network/settings/server/${serverId}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch server network configuration');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update hostname for a specific server
 */
export async function updateServerHostname(serverId: number | string, hostname: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/network/settings/server/${serverId}/hostname`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify({ hostname }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update hostname');
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to update hostname');
  }
}

/**
 * Fetch network interfaces for a specific server
 * This function is used by useNetworkSettingList hook
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchNetworkInterfaces(serverId: number | string): Promise<{ data: any[] }> {
  const response = await fetch(
    buildApiUrl(`/api/admin/network/settings/server/${serverId}/interfaces`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch network interfaces');
  }

  const result = await response.json();
  return { data: result.data || [] };
}

